import { FormEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { sendMagicLink } from "../lib/api";
import { getAppUrl, isPreviewEnvironment } from "../lib/config";
import { storageKeys } from "../lib/storage";
import { useInstrumentation } from "../hooks/useInstrumentation";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";

export default function SignIn() {
  const [searchParams] = useSearchParams();
  const instrumentation = useInstrumentation();
  const isPreview = isPreviewEnvironment();

  useEffect(() => {
    instrumentation.track("Sign In Page Viewed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [devLink, setDevLink] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const returnUrl = searchParams.get("returnUrl");
      const verifyUrl = new URL("/auth/verify", getAppUrl());
      if (returnUrl) verifyUrl.searchParams.set("returnUrl", returnUrl);
      const result = await sendMagicLink(email, verifyUrl.toString());
      setSent(true);

      // If backend returns the link (dev/preview mode), show it
      if (result.signInLink) {
        setDevLink(result.signInLink);
      }

      // Store email for verification page
      localStorage.setItem(storageKeys.emailForSignIn, email);
    } catch (err) {
      const error = err as {
        response?: { data?: { error?: { message?: string } } };
      };
      setError(error.response?.data?.error?.message || "Failed to send magic link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-full flex items-center justify-center p-4 bg-background">
        <Card className="p-10 max-w-lg w-full">
          <header className="text-center mb-8">
            <h1 className="text-5xl font-bold text-primary mb-1">NOMATO</h1>
            <p className="text-lg text-muted-foreground">Off the beaten path</p>
          </header>

          <h2 className="text-2xl font-semibold mb-4 text-foreground">Check your email</h2>
          <p className="mb-2 text-muted-foreground">
            We&apos;ve sent a magic link to <strong className="text-foreground">{email}</strong>
          </p>
          <p className="mb-6 text-muted-foreground">Click the link in the email to sign in.</p>

          {devLink && (
            <div className="my-6 p-4 bg-accent rounded-md">
              <p className="font-semibold text-accent-foreground mb-2 text-sm">
                Development/Preview Mode - Magic Link:
              </p>
              <a
                href={devLink}
                className="block break-all text-primary no-underline hover:underline text-sm"
                aria-label="Sign in with development magic link"
              >
                {devLink}
              </a>
            </div>
          )}

          <Button onClick={() => setSent(false)} variant="secondary" size="lg">
            Send another link
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full flex items-center justify-center p-4 bg-background">
      <Card className="p-10 max-w-lg w-full">
        <header className="text-center mb-8">
          <h1 className="text-5xl font-bold text-primary mb-1">NOMATO</h1>
          <p className="text-lg text-muted-foreground">Off the beaten path</p>
        </header>

        <h2 className="text-2xl font-semibold mb-2 text-foreground">Sign in to your account</h2>
        <p className="text-muted-foreground mb-6 leading-normal">
          Enter your email and we&apos;ll send you a magic link to sign in.
        </p>

        {isPreview && (
          <Alert className="mb-6">
            <AlertDescription>
              <strong>Preview environment</strong> — Sign-in requires manual setup. Add this URL to Firebase authorized
              domains and Mapbox allowed origins.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" disabled={loading} size="lg" className="w-full">
            {loading ? "Sending..." : "Send magic link"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
